// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a form component for users to update their personal account
 * details, such as their first and last name. The email address is displayed but
 * cannot be modified as it is used for authentication.
 */
import * as z from 'zod';
import {
  UpdatePersonParameters,
  updatePerson,
} from '@/account/api/update-person';
import { User } from '@/authentication/api/types';
import { FormCard } from '@/components/form-card';
import { FormCardRow } from '@/components/form-card-row';
import { FormTextField } from '@/components/form-text-field';
import { zodResolver } from '@hookform/resolvers/zod';
import { Divider, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';

/**
 * Schema for validating form input data.
 * Ensures both first and last names are provided.
 */
const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

/** Type representing the validated form data */
type FormData = z.infer<typeof formSchema>;

interface Props {
  /**
   * The current application user.
   */
  user: User;
}

/**
 * A form component that allows the current user to update their first and last
 * name. The email address is displayed but cannot be modified as it is used
 * for authentication.
 *
 * Changes are saved only when explicitly submitted, and the form tracks dirty
 * state to enable/disable the save button appropriately.
 */
export function AccountDetailsCard(props: Props) {
  const queryClient = useQueryClient();

  const updatePersonMutation = useMutation({
    mutationFn: (parameters: UpdatePersonParameters) =>
      updatePerson(props.user.key, parameters),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      reset({
        firstName: data.person.firstName,
        lastName: data.person.lastName,
      });
    },
  });

  const { control, formState, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: props.user.firstName,
      lastName: props.user.lastName,
    },
  });

  const isSaveDisabled = updatePersonMutation.isPending || !formState.isDirty;

  /**
   * Resets the form to its initial state, discarding any changes.
   */
  const handleReset = () => {
    reset();
  };

  /**
   * Handles form submission when the data is valid.
   * Only sends changed fields to the API to minimize unnecessary updates.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const parameters: UpdatePersonParameters = {};

    if (formState.dirtyFields.firstName) {
      parameters.firstName = data.firstName;
    }

    if (formState.dirtyFields.lastName) {
      parameters.lastName = data.lastName;
    }

    updatePersonMutation.mutate(parameters);
  };

  return (
    <FormCard
      title="Details"
      isDirty={formState.isDirty}
      isDisabled={isSaveDisabled}
      onReset={handleReset}
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      <FormCardRow
        title="Email"
        subtitle="The email address used to sign in to Hardfin"
      >
        <TextField
          size="small"
          label="Email"
          InputLabelProps={{ shrink: true }}
          disabled
          value={props.user.email}
          sx={{ maxWidth: { xs: '100%', sm: 300 } }}
        />
      </FormCardRow>
      <Divider />
      <FormCardRow
        title="Name"
        subtitle="The name used to identify you within your organization"
      >
        <Stack spacing={{ xs: 2, sm: 2 }}>
          <FormTextField
            name="firstName"
            label="First name"
            control={control}
            disabled={updatePersonMutation.isPending}
            sx={{ maxWidth: { xs: '100%', sm: 300 } }}
          />
          <FormTextField
            name="lastName"
            label="Last name"
            control={control}
            disabled={updatePersonMutation.isPending}
            sx={{ maxWidth: { xs: '100%', sm: 300 } }}
          />
        </Stack>
      </FormCardRow>
    </FormCard>
  );
}
